import { validCompensationRange } from '../utils/remuneration';
// Hook: gère l'état du formulaire de candidature, la validation
// et l'API de mise à jour des champs.
// Utilisation:
//   const { form, errors, update, validate, setLang } = useForm(initialLang)
import { useCallback, useState, useRef } from 'react';
import { Lang } from '../type/language';
import { FormState } from '../type/form-step';
import { emailValid, phoneValid } from '../utils/get-query-params';
import { isImageFile, isPdfFile } from '../utils/file-validation';
import { OfferPrefill } from './useOffer';

export function useForm(initialLang: Lang, initialSource = '', offerPrefill?: OfferPrefill) {
  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    name: '',
    role: offerPrefill?.role || '',
    email: '',
    phone: '',
    address: '',
    profileImage: null,
    resume: null,
    cvAiConsent: true,
    schoolLevel: offerPrefill?.schoolLevel || '',
    languages: [],
    languageCode: offerPrefill?.languageCode || '',
    languageLevel: 'fluent',
    experienceMonths: offerPrefill?.experienceMonths || '',
    availableDate: offerPrefill?.availableDate || 'NOW',
    mobile: true,
    rayonKm: '',
    city: offerPrefill?.city || '',
    workType: offerPrefill?.workType || [],
    contractType: offerPrefill?.contractType ? [offerPrefill.contractType] : [],
    minSalary: offerPrefill?.contractType === 'freelance' ? '' : offerPrefill?.minSalary || '',
    dailyRateMin: offerPrefill?.contractType === 'freelance' ? offerPrefill.minSalary : '',
    dailyRateMax: offerPrefill?.contractType === 'freelance' ? offerPrefill.maxSalary : '',
    maxSalary: offerPrefill?.contractType === 'freelance' ? '' : offerPrefill?.maxSalary || '',
    currency: offerPrefill?.currency || 'EUR',
    acceptCGU: false,
    lang: initialLang,
    source: initialSource,
  });
  const touched = useRef(new Set<keyof FormState>());
  const [errors, setErrors] = useState<Record<string, string>>({});

  const update = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    touched.current.add(key);
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const patch = useCallback((values: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...values }));
  }, []);

  const applyResumePrefill = useCallback((values: Partial<FormState>) => {
    setForm((previous) => {
      const next = { ...previous };
      for (const [key, value] of Object.entries(values)) {
        if (!touched.current.has(key as keyof FormState)) (next as unknown as Record<string, unknown>)[key] = value;
      }
      next.name = `${next.firstName} ${next.lastName}`.trim();
      return next;
    });
  }, []);

  const setLang = useCallback((lang: Lang) => {
    update('lang', lang);
  }, [update]);

  function validate(t: Record<string, string>): boolean {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = t.required;
    if (!form.lastName.trim()) e.lastName = t.required;
    if (!form.role.trim()) e.role = t.required;
    if (!form.email.trim()) e.email = t.required;
    else if (!emailValid(form.email)) e.email = t.invalidEmail;
    if (!form.phone.trim()) e.phone = t.required;
    else if (!phoneValid(form.phone)) e.phone = t.invalidPhone;
    if (!form.resume) e.resume = t.required;
    else if (!isPdfFile(form.resume)) {
      e.resume = t.invalidResumePdf;
    }
    if (form.profileImage && !isImageFile(form.profileImage)) {
      e.profileImage = t.invalidProfileImage;
    }
    if (form.profileImage && form.profileImage.size > 5 * 1024 * 1024) {
      e.profileImage = t.invalidProfileImageSize;
    }
    if (!validCompensationRange(form.minSalary, form.maxSalary) || !validCompensationRange(form.dailyRateMin, form.dailyRateMax)) {
      e.salary = t.invalidSalaryRange;
    }
    const rayonKm = Number(form.rayonKm || '0');
    if (form.rayonKm && (Number.isNaN(rayonKm) || rayonKm < 0)) {
      e.rayonKm = t.invalidRadius;
    }
    if (form.languages.some((language) => !language.level)) e.languages = t.required;
    if (!form.acceptCGU) e.acceptCGU = t.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  return { applyResumePrefill, form, errors, update, patch, validate, setLang, setErrors };
}
